import {
  Accordion,
  ActionIcon,
  Anchor,
  Button,
  Drawer,
  DrawerProps,
  Group,
  List,
  Stack,
} from "@mantine/core";
import { IconChevronRight, IconX } from "@tabler/icons-react";
import React from "react";

import { Text, Title } from "@components";

import { careers } from "@data";

interface CareersDrawerProps extends Omit<DrawerProps, "children"> {
  onBack?: () => void;
  onCloseStack?: () => void;
}

export function CareersDrawer({
  onBack,
  onCloseStack,
  ...drawerProps
}: CareersDrawerProps) {
  return (
    <Drawer
      position="right"
      size="lg"
      padding="xl"
      offset={12}
      radius="sm"
      overlayProps={{ backgroundOpacity: onBack ? 0 : 0.9 }}
      closeButtonProps={{
        bg: "transparent",
        icon: <IconX size={24} />,
        size: "lg",
        onClick: onCloseStack,
      }}
      styles={{
        header: {
          paddingTop: "var(--mantine-spacing-lg)",
          paddingBottom: "var(--mantine-spacing-lg)",
          paddingLeft: onBack ? "var(--mantine-spacing-md)" : undefined,
          borderBottom: "1px solid var(--mantine-color-decorativeBorder-0)",
          background: "var(--mantine-color-layoutBody-0)",
          boxShadow: "inset 0 1px 1px 0 rgb(255 255 255 / 25%)",
        },
        body: {
          paddingTop: "var(--mantine-spacing-xl)",
          background: "var(--mantine-color-layoutBody-0)",
        },
        content: {
          background: "var(--mantine-color-layoutBody-0)",
          boxShadow: "0 1px 1px 0 rgb(0 0 0 / 25%)",
        },
        inner: {
          right: 0,
        },
        close: {
          color: "var(--mantine-color-fontDark-0)",
        },
      }}
      title={
        onBack ? (
          <Group>
            <ActionIcon
              variant="subtle"
              onClick={onBack}
              size="lg"
              radius="xl"
              styles={{
                root: {
                  color: "var(--mantine-color-fontDark-0)",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.1)",
                  },
                },
              }}
            >
              <IconChevronRight
                size={24}
                style={{ transform: "rotate(180deg)" }}
              />
            </ActionIcon>
            <Title order={5} c="fontDark">
              Career&nbsp;Opportunities
            </Title>
          </Group>
        ) : (
          <Title order={5} c="fontDark">
            Career Opportunities
          </Title>
        )
      }
      {...drawerProps}
    >
      <Stack gap="lg">
        <Text c="fontDark" size="xs">
          At Airtight Design, we're excited to meet talented developers,
          designers, and problem-solvers. Browse any open roles below and reach
          out directly at{" "}
          <Anchor
            href="mailto:jobs@airtightdesign.com"
            c="brandPrimary"
            inherit
          >
            jobs@airtightdesign.com
          </Anchor>
          .
        </Text>

        <Accordion
          variant="contained"
          styles={{
            item: {
              borderColor: "var(--mantine-color-decorativeBorder-0)",
            },
            control: {
              height: 60,
              paddingLeft: "var(--mantine-spacing-lg)",
              paddingRight: "var(--mantine-spacing-lg)",
            },
            content: {
              paddingLeft: "var(--mantine-spacing-lg)",
              paddingRight: "var(--mantine-spacing-lg)",
              paddingBottom: "var(--mantine-spacing-lg)",
            },
          }}
        >
          {careers.map((career) => (
            <Accordion.Item key={career.id} value={career.id}>
              <Accordion.Control>
                <Text size="xs" c="fontDark">
                  {career.title}
                </Text>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <Text size="xs" c="fontDark">
                    {career.description}
                  </Text>

                  {career.requirements && (
                    <>
                      <Text size="xs" fw={600} c="fontDark">
                        Requirements:
                      </Text>
                      <List size="xs" spacing="xs" ml="xs">
                        {career.requirements.map((req, index) => (
                          <List.Item key={index}>
                            <Text size="xs" c="fontDark" pr="lg">
                              {req}
                            </Text>
                          </List.Item>
                        ))}
                      </List>
                    </>
                  )}
                  <Button
                    variant="flat-square"
                    size="xs"
                    mt="sm"
                    component="a"
                    href={`mailto:jobs@airtightdesign.com?subject=Application: ${career.title}`}
                  >
                    Apply for this position
                  </Button>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion>

        <Text c="fontDark" size="xs" fs="italic">
          Don't see the perfect fit right now? Send us your résumé and a quick
          note instead. We are always interested in hearing from great people
          and future collaborators.
        </Text>
      </Stack>
    </Drawer>
  );
}
